import React from 'react';
import { formatCurrency, formatPercentage } from '../../utils/calculations';

const ResultSummary = ({ results }) => {
  const formatDecimal = (value, decimals = 2) => {
    if (!value) return "0";
    
    // Handle Decimal objects
    if (typeof value === 'object' && value.toString) {
      return value.toFixed ? value.toFixed(decimals) : value.toString();
    }
    
    // Handle regular numbers
    return typeof value === 'number' ? value.toFixed(decimals) : value.toString();
  };
  
  // Helper function to render APR information
  const renderAPRInfo = () => {
    return (
      <div className="mb-6">
        <h3 className="font-semibold text-sm uppercase text-gray-600 mb-2">Interest Rate Details</h3>
        <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {results.monthlyInterestRate && (
              <div>
                <p className="text-sm text-gray-600">Monthly Rate</p>
                <p className="text-xl font-semibold">{formatDecimal(results.monthlyInterestRate, 2)}%</p>
              </div>
            )}
            
            <div>
              <p className="text-sm text-gray-600">Annual Rate</p>
              <p className="text-xl font-semibold">{formatDecimal(results.annualInterestRate, 2)}%</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-600">APR</p>
              <p className="text-xl font-semibold">{formatPercentage(results.apr, 2)}</p>
            </div>

            {results.effectiveAPR && (
              <div>
                <p className="text-sm text-gray-600">Effective APR</p>
                <p className="text-xl font-semibold">{formatPercentage(results.effectiveAPR, 2)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {results.calculatorType === 'missed' && 'Includes impact of missed payments & late fees'}
                  {results.calculatorType === 'interest' && 'Adjusted for interest freeze period'}
                  {results.calculatorType === 'restructure' && 'Based on restructured terms'}
                  {results.calculatorType === 'early' && 'Adjusted for early repayment'}
                </p>
              </div>
            )}
          </div>
          
          {/* Daily Interest Information */}
          {results.dailyInterestRate && results.dailyInterestAmount && (
            <div className="mt-4 pt-4 border-t border-blue-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Daily Interest</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Daily Rate</p>
                  <p className="text-lg font-semibold">{formatDecimal(results.dailyInterestRate, 4)}%</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Daily Interest Amount</p>
                  <p className="text-lg font-semibold">{formatCurrency(results.dailyInterestAmount)}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Basic repayment information for all calculator types
  const renderBasicInfo = () => {
    // Handle restructuring results differently
    if (results.calculatorType === 'restructure') {
      const loanTerm = results.newTermMonths || results.remainingMonths;
      const monthlyPayment = results.newMonthlyPayment;
      const totalRepaid = results.newTotalCost || (monthlyPayment * loanTerm);
      
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <p className="text-sm text-gray-600">Principal Amount</p>
            <p className="text-xl font-semibold">{formatCurrency(results.currentBalance)}</p>
          </div>
          
          <div>
            <p className="text-sm text-gray-600">Loan Term</p>
            <p className="text-xl font-semibold">{String(loanTerm)} months</p>
          </div>
          
          <div>
            <p className="text-sm text-gray-600">Monthly Payment</p>
            <p className="text-xl font-semibold">{formatCurrency(monthlyPayment)}</p>
          </div>
          
          <div>
            <p className="text-sm text-gray-600">Total Interest</p>
            <p className="text-xl font-semibold">{formatCurrency(results.totalInterest)}</p>
          </div>
          
          <div>
            <p className="text-sm text-gray-600">Total Repaid</p>
            <p className="text-xl font-semibold">{formatCurrency(totalRepaid)}</p>
          </div>
        </div>
      );
    }
    
    // Default handling for other calculator types
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div>
          <p className="text-sm text-gray-600">Principal Amount</p>
          <p className="text-xl font-semibold">{formatCurrency(results.principal || results.currentBalance)}</p>
        </div>
        
        <div>
          <p className="text-sm text-gray-600">Loan Term</p>
          <p className="text-xl font-semibold">{String(results.termInMonths || results.remainingMonths)} months</p>
        </div>
        
        <div>
          <p className="text-sm text-gray-600">Monthly Payment</p>
          <p className="text-xl font-semibold">{formatCurrency(results.monthlyPayment || results.currentMonthlyPayment)}</p>
        </div>
        
        <div>
          <p className="text-sm text-gray-600">Total Interest</p>
          <p className="text-xl font-semibold">{formatCurrency(results.totalInterest || results.currentTotalInterest)}</p>
        </div>
        
        <div>
          <p className="text-sm text-gray-600">Total Repaid</p>
          <p className="text-xl font-semibold">{formatCurrency(results.totalRepaid || results.currentTotalRepaid)}</p>
        </div>
      </div>
    );
  };

  // Early repayment specific information
  const renderEarlyRepaymentInfo = () => {
    if (results.calculatorType !== 'early') return null;
    
    return (
      <div className="mb-6">
        <h3 className="font-semibold text-sm uppercase text-gray-600 mb-2">Early Repayment Options</h3>
        
        {/* Full Settlement */}
        <div className="bg-green-50 p-4 rounded-md border border-green-100 mb-4">
          <h4 className="text-lg font-semibold text-green-800 mb-3">Full Settlement Today</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Settlement Amount</p>
              <p className="text-xl font-semibold text-green-700">{formatCurrency(results.fullSettlementAmount)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Interest Savings</p>
              <p className="text-xl font-semibold text-green-700">{formatCurrency(results.interestSavingsFromFullSettlement)}</p>
            </div>
          </div>
        </div>

        {/* Partial Payment Scenarios */}
        {results.earlyPaymentAmount > 0 && (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
              <h4 className="text-lg font-semibold text-blue-800 mb-3">
                Early Payment of {formatCurrency(results.earlyPaymentAmount)}
              </h4>
              
              {/* Reduce Payment Scenario */}
              {results.reducePaymentScenario && (
                <div className="mb-4 p-3 bg-white rounded border">
                  <h5 className="font-medium text-gray-800 mb-2">Option 1: Lower Monthly Payments</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">New Monthly Payment</p>
                      <p className="text-lg font-semibold text-blue-700">{formatCurrency(results.reducePaymentScenario.newMonthlyPayment)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Monthly Savings</p>
                      <p className="text-lg font-semibold text-green-600">{formatCurrency(results.reducePaymentScenario.monthlySavings)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Total Interest Savings</p>
                      <p className="text-lg font-semibold text-green-600">{formatCurrency(results.reducePaymentScenario.totalSavings)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Reduce Term Scenario */}
              {results.reduceTermScenario && !results.reduceTermScenario.error && (
                <div className="p-3 bg-white rounded border">
                  <h5 className="font-medium text-gray-800 mb-2">Option 2: Shorter Loan Term</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">New Term</p>
                      <p className="text-lg font-semibold text-blue-700">{results.reduceTermScenario.newTermMonths} months</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Months Saved</p>
                      <p className="text-lg font-semibold text-green-600">{results.reduceTermScenario.monthsSaved} months</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Total Interest Savings</p>
                      <p className="text-lg font-semibold text-green-600">{formatCurrency(results.reduceTermScenario.totalSavings)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error message for reduce term scenario */}
              {results.reduceTermScenario && results.reduceTermScenario.error && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">{results.reduceTermScenario.error}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Missed payment specific information
  const renderMissedPaymentsInfo = () => {
    if (results.calculatorType !== 'missed') return null;
    
    return (
      <div className="mb-6">
        <h3 className="font-semibold text-sm uppercase text-gray-600 mb-2">Missed Payments Impact</h3>
        <div className="bg-yellow-50 p-4 rounded-md border border-yellow-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-600">Missed Payments</p>
              <p className="text-xl font-semibold">{results.missedPayments.join(', ')}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-600">Original Term</p>
              <p className="text-xl font-semibold">{String(results.originalTermMonths)} months</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-600">New Term</p>
              <p className="text-xl font-semibold">{String(results.newTermMonths)} months</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-600">Additional Months</p>
              <p className="text-xl font-semibold">{String(results.additionalMonths)} months</p>
            </div>
            
            {results.lateFee > 0 && (
              <>
                <div>
                  <p className="text-sm text-gray-600">Late Fee per Missed Payment</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.lateFee)}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Total Late Fees</p>
                  <p className="text-xl font-semibold text-red-600">{formatCurrency(results.totalLateFees)}</p>
                </div>
              </>
            )}
          </div>
          
          {/* Interest Breakdown for Letters - Key Information */}
          <div className="border-t border-yellow-200 pt-4 mb-4 bg-white p-4 rounded">
            <h4 className="text-base font-semibold text-gray-800 mb-3">📋 Interest Breakdown (For Letters & Documentation)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-orange-50 p-3 rounded border border-orange-200">
                <p className="text-gray-700 font-medium mb-1">Additional Interest from Missed Payments:</p>
                <p className="text-2xl font-bold text-orange-700">{formatCurrency(results.additionalInterest)}</p>
                <p className="text-xs text-gray-600 mt-1">Total extra interest due to missed payments</p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-gray-700 font-medium mb-1">Interest from Extended Term:</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(results.extendedTermInterestOnly)}</p>
                <p className="text-xs text-gray-600 mt-1">Interest accrued during the {results.additionalMonths} additional months</p>
              </div>
              
              {results.lateFee > 0 && (
                <div className="bg-red-50 p-3 rounded border border-red-200">
                  <p className="text-gray-700 font-medium mb-1">Interest Cost of Late Fees:</p>
                  <p className="text-2xl font-bold text-red-700">{formatCurrency(results.lateFeeInterestCost)}</p>
                  <p className="text-xs text-gray-600 mt-1">Interest charged on the late fees themselves</p>
                </div>
              )}
              
              <div className="bg-gray-50 p-3 rounded border border-gray-300">
                <p className="text-gray-700 font-medium mb-1">Value of Missed Payments:</p>
                <p className="text-2xl font-bold text-gray-700">{formatCurrency(results.missedPaymentsTotal)}</p>
                <p className="text-xs text-gray-600 mt-1">Total amount not paid during missed months</p>
              </div>
            </div>
          </div>
          
          {/* Show simple cost comparison */}
          <div className="border-t border-yellow-200 pt-3 mb-3">
            <h4 className="text-sm font-medium text-yellow-800 mb-2">Total Cost Comparison:</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-3">
              <div className="bg-white p-3 rounded border text-center">
                <p className="text-gray-600">Original Total Cost:</p>
                <p className="font-semibold text-green-600 text-lg">{formatCurrency(results.originalTotalRepaid || 0)}</p>
                <p className="text-xs text-gray-500">{results.originalTermMonths} months</p>
              </div>
              <div className="bg-white p-3 rounded border text-center">
                <p className="text-gray-600">New Total Cost:</p>
                <p className="font-semibold text-red-600 text-lg">{formatCurrency(results.totalRepaid || 0)}</p>
                <p className="text-xs text-gray-500">{results.newTermMonths} months</p>
              </div>
              <div className="bg-red-50 p-3 rounded border text-center border-red-200">
                <p className="text-gray-600">Extra Cost:</p>
                <p className="font-semibold text-red-600 text-xl">
                  {formatCurrency((results.totalRepaid || 0) - (results.originalTotalRepaid || 0))}
                </p>
                <p className="text-xs text-red-600 font-medium">
                  {(((results.totalRepaid || 0) - (results.originalTotalRepaid || 0)) / (results.originalTotalRepaid || 1) * 100).toFixed(1)}% more expensive
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-yellow-200 pt-3">
            <p className="text-sm text-yellow-800">
              <strong>Impact Summary:</strong> Missing {results.missedPayments.length} payment{results.missedPayments.length > 1 ? 's' : ''} extends your loan by {String(results.additionalMonths)} months and increases your total cost by {formatCurrency((results.totalRepaid || 0) - (results.originalTotalRepaid || 0))}. The monthly payment amount remains unchanged at {formatCurrency(results.monthlyPayment)}.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Interest accrual specific information
  const renderInterestAccrualInfo = () => {
    if (results.calculatorType !== 'interest' || !results.includeInterestFreeze) return null;
    
    return (
      <div className="mb-6">
        <h3 className="font-semibold text-sm uppercase text-gray-600 mb-2">Interest Freeze Impact</h3>
        <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Freeze Period</p>
              <p className="text-xl font-semibold">
                Months {String(results.freezeStartMonth)} - {String(results.freezeEndMonth)}
              </p>
            </div>
            
            <div>
              <p className="text-sm text-gray-600">Interest Savings</p>
              <p className="text-xl font-semibold">{formatCurrency(results.interestSavings)}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-600">Method</p>
              <p className="text-xl font-semibold capitalize">{results.interestType}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Restructuring specific information
  const renderRestructuringInfo = () => {
    if (results.calculatorType !== 'restructure') return null;
    
    const isZeroArrears = results.arrearsAmount === 0 || results.arrearsAmount?.isZero?.();
    
    return (
      <div className="mb-6">
        <h3 className="font-semibold text-sm uppercase text-gray-600 mb-2">Restructuring Impact</h3>
        
        {/* Show original loan information if available */}
        {(results.originalPrincipal || results.originalTermMonths) && (
          <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mb-4">
            <h4 className="font-medium text-gray-700 mb-2">Original Loan Terms (for comparison)</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {results.originalPrincipal && (
                <div>
                  <p className="text-gray-600">Original Amount</p>
                  <p className="font-semibold">{formatCurrency(results.originalPrincipal)}</p>
                </div>
              )}
              {results.originalTermMonths && (
                <div>
                  <p className="text-gray-600">Original Term</p>
                  <p className="font-semibold">{String(results.originalTermMonths)} months</p>
                </div>
              )}
              <div>
                <p className="text-gray-600">Original Monthly Payment</p>
                <p className="font-semibold">{formatCurrency(results.originalMonthlyPayment)}</p>
              </div>
            </div>
            
            {/* Show date-based calculation info if used */}
            {results.dateBasedCalculation && (
              <div className="mt-3 pt-3 border-t border-gray-300">
                <p className="text-xs text-blue-600 font-medium mb-2">
                  ✓ Enhanced date-based calculation used for maximum precision
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-600">
                  <div>
                    <span className="font-medium">Exact months elapsed:</span> {results.dateBasedCalculation.exactMonthsElapsed?.toFixed(2)}
                  </div>
                  <div>
                    <span className="font-medium">Payments completed:</span> {results.dateBasedCalculation.paymentsCompleted}
                  </div>
                  {results.dateBasedCalculation.daysSinceLastPayment > 0 && (
                    <div>
                      <span className="font-medium">Days since last payment:</span> {Math.round(results.dateBasedCalculation.daysSinceLastPayment)}
                    </div>
                  )}
                  {results.dateBasedCalculation.adjustedForTiming && (
                    <div className="col-span-2">
                      <span className="text-amber-600 font-medium">⚠ Calculation adjusted for payment timing</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        
        {isZeroArrears && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Proactive Planning:</strong> This calculation shows potential restructuring options without current arrears.
            </p>
          </div>
        )}
        <div className="bg-green-50 p-4 rounded-md border border-green-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {results.restructureType === 'spread' && (
              <>
                <div>
                  <p className="text-sm text-gray-600">{isZeroArrears ? 'Current Outstanding Balance' : 'Arrears Amount'}</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.arrearsAmount)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Current Payment (without arrears)</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.currentPaymentWithoutArrears || results.originalMonthlyPayment)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">New Payment (with arrears spread)</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.newMonthlyPayment)}</p>
                </div>
                {!isZeroArrears && (
                  <>
                    <div>
                      <p className="text-sm text-gray-600">Payment Increase</p>
                      <p className="text-xl font-semibold">{formatCurrency(results.increasedMonthlyAmount)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Percentage Increase</p>
                      <p className="text-xl font-semibold">{formatPercentage(results.percentageIncrease)}</p>
                    </div>
                  </>
                )}
                {results.additionalCost && results.additionalCost !== 0 && (
                  <div>
                    <p className="text-sm text-gray-600">Additional Total Cost</p>
                    <p className="text-xl font-semibold text-red-600">{formatCurrency(results.additionalCost)}</p>
                  </div>
                )}
                {results.additionalInterest && results.additionalInterest !== 0 && (
                  <div>
                    <p className="text-sm text-gray-600">Additional Interest Cost</p>
                    <p className="text-xl font-semibold text-orange-600">{formatCurrency(results.additionalInterest)}</p>
                  </div>
                )}
                {results.originalTotalCost && (
                  <div>
                    <p className="text-sm text-gray-600">Original Total Cost</p>
                    <p className="text-lg font-medium">{formatCurrency(results.originalTotalCost)}</p>
                  </div>
                )}
                {isZeroArrears && (!results.additionalCost || results.additionalCost === 0) && (
                  <div className="col-span-2">
                    <p className="text-sm text-gray-600">
                      This shows your current loan structure. To restructure, choose "Extend Term" or "Reduce Payment" options.
                    </p>
                  </div>
                )}
              </>
            )}
            
            {results.restructureType === 'extend' && (
              <>
                <div>
                  <p className="text-sm text-gray-600">{isZeroArrears ? 'Current Balance' : 'Arrears Amount'}</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.arrearsAmount)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">New Term Length</p>
                  <p className="text-xl font-semibold">{String(results.newTermMonths)} months</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Additional Months</p>
                  <p className="text-xl font-semibold">{String(results.additionalMonths)} months</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Monthly Payment</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.newMonthlyPayment)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Additional Total Cost</p>
                  <p className="text-xl font-semibold text-red-600">{formatCurrency(results.additionalCost)}</p>
                </div>
                {results.additionalInterest && results.additionalInterest !== 0 && (
                  <div>
                    <p className="text-sm text-gray-600">Additional Interest Cost</p>
                    <p className="text-xl font-semibold text-orange-600">{formatCurrency(results.additionalInterest)}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">Original Total Cost</p>
                  <p className="text-lg font-medium">{formatCurrency(results.originalTotalCost)}</p>
                </div>
              </>
            )}
            
            {results.restructureType === 'reduce' && (
              <>
                <div>
                  <p className="text-sm text-gray-600">{isZeroArrears ? 'Current Balance' : 'Arrears Amount'}</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.arrearsAmount)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">New Term Length</p>
                  <p className="text-xl font-semibold">{String(results.newTermMonths)} months</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Original Payment</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.originalMonthlyPayment)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Reduced Payment</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.newMonthlyPayment)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Payment Reduction</p>
                  <p className="text-xl font-semibold">{formatCurrency(results.paymentReduction)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Percentage Reduction</p>
                  <p className="text-xl font-semibold">{formatPercentage(results.percentageReduction)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Additional Total Cost</p>
                  <p className="text-xl font-semibold text-red-600">{formatCurrency(results.additionalCost)}</p>
                </div>
                {results.additionalInterest && results.additionalInterest !== 0 && (
                  <div>
                    <p className="text-sm text-gray-600">Additional Interest Cost</p>
                    <p className="text-xl font-semibold text-orange-600">{formatCurrency(results.additionalInterest)}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">Original Total Cost</p>
                  <p className="text-lg font-medium">{formatCurrency(results.originalTotalCost)}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-8">
      <h2 className="text-xl font-bold mb-6 text-gray-800">Calculation Summary</h2>
      
      {/* Date Information if provided */}
      {results.dateInfo && results.dateInfo.startDate && (
        <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">📅 Loan Timeline</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600 font-medium">Loan Start Date</p>
              <p className="text-lg font-semibold text-purple-700">
                {new Date(results.dateInfo.startDate).toLocaleDateString('en-GB', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">First Payment Date</p>
              <p className="text-lg font-semibold text-purple-700">
                {new Date(results.dateInfo.firstPaymentDate).toLocaleDateString('en-GB', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            <div>
              <p className="text-gray-600 font-medium">Final Payment Date</p>
              <p className="text-lg font-semibold text-purple-700">
                {new Date(results.dateInfo.endDate).toLocaleDateString('en-GB', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
          <p className="mt-3 text-xs text-purple-700">
            💡 Dates help account for varying month lengths (28-31 days) for more accurate calculations.
          </p>
        </div>
      )}
      
      {results.calculatorType !== 'early' && renderAPRInfo()}
      {results.calculatorType !== 'early' && renderBasicInfo()}
      {renderEarlyRepaymentInfo()}
      {renderMissedPaymentsInfo()}
      {renderInterestAccrualInfo()}
      {renderRestructuringInfo()}
    </div>
  );
};

export default ResultSummary;